import React, { FC, useCallback, useMemo } from 'react';

import pluralize from 'pluralize';

import {
	MOBILE_PLATFORM_FILTER_ID,
	parseMobilePlatform
} from 'src/apps/dashboard/constants/MobileDashboard.constants';
import { MOBILE_FILTER_DEFINITIONS } from 'src/apps/dashboard/constants/MobileDashboardDefaults';
import { buildLabelsExcludedFromDashboard } from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { useMobileAppIdFilterOptionsQuery } from 'src/apps/dashboard/hooks/useMobileAppIdFilterOptionsQuery';
import { useMobileBusinessUnitsTableData } from 'src/apps/dashboard/hooks/useMobileBusinessUnitsTableData';
import { useMobileDashboardDataQuery } from 'src/apps/dashboard/hooks/useMobileDashboardDataQuery';
import { useMobileScoreDataQuery } from 'src/apps/dashboard/hooks/useMobileScoreDataQuery';
import { getMobileAppIdsFromFilters } from 'src/apps/dashboard/services/queries/mobile/helpers/mobileFilterVariableBuilder';
import { useMobileDashboardFiltersStore } from 'src/apps/dashboard/stores/mobileDashboardFiltersStore';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { isQueryLoading } from 'src/common/helpers/QueryHelpers';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { useDashboardFiltersStore } from 'src/common/stores/dashboardFiltersStore';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { HEADER } from './MobileAccessibilityDashboard.constants';

export const MobileAccessibilityDashboard: FC = () => {
	usePlatformConfigInitializer();
	const { filters, setFilters } = useDashboardFiltersStore();
	const { setPlatform, setAppIds } = useMobileDashboardFiltersStore();
	const { isUniquenessServiceEnabled, mobileCoreLabels, unitIdDisplayName } =
		usePlatformConfigStore();

	const labelsExcludedFromDashboard = useMemo(
		() => buildLabelsExcludedFromDashboard(mobileCoreLabels),
		[mobileCoreLabels]
	);

	const effectiveFilters = useEffectiveFilters(filters, MOBILE_FILTER_DEFINITIONS);

	const dashboardDataQuery = useMobileDashboardDataQuery(
		filters,
		MOBILE_FILTER_DEFINITIONS,
		labelsExcludedFromDashboard
	);
	const scoreDataQuery = useMobileScoreDataQuery(
		filters,
		MOBILE_FILTER_DEFINITIONS,
		labelsExcludedFromDashboard
	);
	const [businessUnitsTableQuery] = useMobileBusinessUnitsTableData({
		filters,
		filterDefinitions: MOBILE_FILTER_DEFINITIONS
	});

	const isBusinessUnitsLoading = isQueryLoading(businessUnitsTableQuery);

	const handleFilterChange = useCallback(
		(newFilters: IFilterOptions) => {
			const platformValue = newFilters[MOBILE_PLATFORM_FILTER_ID]?.[0]?.value;
			setPlatform(parseMobilePlatform(platformValue));
			setAppIds(getMobileAppIdsFromFilters(newFilters));
			setFilters(newFilters);
		},
		[setAppIds, setFilters, setPlatform]
	);

	const filterOptions = useMobileAppIdFilterOptionsQuery();

	const filter = useMemo(
		() => ({
			filters,
			filterDefinitions: MOBILE_FILTER_DEFINITIONS,
			filterOptions,
			onFilterChange: handleFilterChange
		}),
		[filters, filterOptions, handleFilterChange]
	);

	const scoreQuery = useMemo(
		() => ({
			data: scoreDataQuery.data,
			isLoading: scoreDataQuery.isLoading,
			error: scoreDataQuery.error
		}),
		[scoreDataQuery.data, scoreDataQuery.isLoading, scoreDataQuery.error]
	);

	const dashboardQuery = useMemo(
		() => ({
			data: dashboardDataQuery.data,
			isLoading: dashboardDataQuery.isLoading,
			error: dashboardDataQuery.error
		}),
		[dashboardDataQuery.data, dashboardDataQuery.isLoading, dashboardDataQuery.error]
	);

	const bottom = useMemo(
		() => ({
			kind: 'businessUnitsTable' as const,
			title: pluralize(unitIdDisplayName),
			query: {
				data: businessUnitsTableQuery.data,
				isLoading: isBusinessUnitsLoading,
				error: businessUnitsTableQuery.error
			}
		}),
		[
			unitIdDisplayName,
			businessUnitsTableQuery.data,
			isBusinessUnitsLoading,
			businessUnitsTableQuery.error
		]
	);

	return (
		<DashboardTemplate
			header={HEADER}
			filter={filter}
			effectiveFilters={effectiveFilters}
			scoreQuery={scoreQuery}
			dashboardQuery={dashboardQuery}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			showCharts={false}
			bottom={bottom}
		/>
	);
};
